import styled from '@emotion/styled';
import {
  DSButton,
  DSContextMenu,
  DSSearchInput,
} from '@hundred5/design-system';
import React, { useMemo, useRef, useState } from 'react';

import { getAmplitudeClient } from '@/features/amplitude';
import { useClickOutside } from '@/features/common/hooks/use-click-outside';
import { useWorkspaceId } from '@/hooks/router';
import { getJobopeningCareersQueryKey } from '@/queries/jobopenings/keys';
import { usePatchJobopeningDescriptionMutation } from '@/queries/jobopenings/mutations';
import { useGetJobopeningCareersAvailable } from '@/queries/jobopenings/queries';
import { JobopeningCareersAvailable } from '@/queries/types';
import { queryClient } from '@/utils/reactQuery';
import { usePostHog } from 'posthog-js/react';

export const CareersAddJobOpeningButton = () => {
  const [isAddJobContextOpen, setIsAddJobContextOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const contextMenuRef = useRef<null | HTMLDivElement>(null);
  const workspaceId = useWorkspaceId();
  const query = useGetJobopeningCareersAvailable(Number(workspaceId));
  const mutation = usePatchJobopeningDescriptionMutation();
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  const filteredQuery: JobopeningCareersAvailable[] = useMemo(() => {
    if (!searchQuery && query.data) {
      return query.data;
    }
    if (query.data) {
      return query.data?.filter((jobOpening) =>
        jobOpening.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return [];
  }, [query.data, searchQuery]);

  useClickOutside(contextMenuRef, () => {
    setIsAddJobContextOpen(false);
  });

  return (
    <Container ref={contextMenuRef}>
      <DSButton
        variant="secondary"
        onClick={() => {
          setIsAddJobContextOpen(true);
          amplitude?.logEvent('careers/add job', {
            'workspace id': workspaceId,
          });
          posthog?.capture('hire_admin_app:careers/add job', {
            'workspace id': workspaceId,
          });
        }}
        disabled={!query.data || query.data.length === 0}
      >
        Add job
      </DSButton>
      <DSContextMenu
        open={isAddJobContextOpen}
        position={{ bottom: '40px', right: '0' }}
        style={{ minWidth: '292px' }}
      >
        {query.data && query.data?.length > 10 && (
          <SearchContainer>
            <DSSearchInput
              placeholder={'Find...'}
              value={searchQuery}
              showResetButton={true}
              reset={() => {
                setSearchQuery('');
              }}
              onChange={(event) => {
                const { value } = event.target;
                setSearchQuery(value);
              }}
              onKeyDown={(event) => {
                if (event.key === ' ' || event.keyCode === 32) {
                  event.stopPropagation();
                }
              }}
              small
            />
          </SearchContainer>
        )}
        {filteredQuery.length ? (
          <DSContextMenu.Title label="Active" />
        ) : (
          <SearchNotFound>No active job openings found</SearchNotFound>
        )}
        <DSContextMenu.Scrollsection height="240px">
          {filteredQuery.map((jobOpening) => (
            <MenuItem
              key={jobOpening.id}
              label={jobOpening.name}
              onClick={() => {
                mutation.mutate(
                  {
                    workspaceId: Number(workspaceId),
                    openingId: Number(jobOpening.id),
                    data: {
                      addToCareersPage: true,
                    },
                  },
                  {
                    onSuccess: () => {
                      setIsAddJobContextOpen(false);
                      queryClient.invalidateQueries({
                        queryKey: getJobopeningCareersQueryKey(
                          Number(workspaceId)
                        ),
                      });
                    },
                  }
                );
              }}
            />
          ))}
        </DSContextMenu.Scrollsection>
      </DSContextMenu>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const MenuItem = styled(DSContextMenu.Item)`
  width: 284px;
`;

const SearchContainer = styled.div`
  padding: 16px 8px 8px 8px;
`;

const SearchNotFound = styled.div`
  padding: 8px;
  color: ${(props) => props.theme.typography.colorSecondary};
`;
